import { useAuth, useEntities } from "@emberly/zenith-client";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FLEET_MGMT_INTEGRATION_QUERY } from "../common/queries";

export function useVehiclesWithFleetMgmt(activeTasks) {
  const { entities: vehicles } = useEntities("ServiceVehicle");
  const { entities: fleetIntegrations } = useEntities("Integration", FLEET_MGMT_INTEGRATION_QUERY);
  const [fleetUnits, setFleetUnits] = useState(new Map());
  const { getAccessTokenSilently } = useAuth();

  useEffect(() => {
    if (fleetIntegrations.length === 0) return;

    let nextIntegrationExpires = null;
    let fleetData = new Map();
    let destroyed = false;

    const fetch = async () => {
      try {

        if (nextIntegrationExpires !== null && nextIntegrationExpires > Date.now() || destroyed) {
          return;
        }

        const res = await axios(
          `/api/integration/fleet/units/list/all`,
          {
            headers: { Authorization: `Bearer ${await getAccessTokenSilently()}` },
          }
        );

        if (destroyed) return;

        const { fleets } = res.data;
        const fleetIds = Object.keys(fleets);

        for (let i = 0; i < fleetIds.length; i++) {

          // fetch and check fleetId
          const fleetId = fleetIds[i];
          if (!fleetId) continue;

          // fetch and check fleet
          const fleet = fleets[fleetId];
          if (!fleet) continue;

          // fetch and check integration
          const integration = fleetIntegrations.find(t => t.id === fleetId);
          if (!integration || !fleet || !integration?.fleetMappings) continue;


          const expires = new Date(fleet.expires);

          if (nextIntegrationExpires === null || expires < nextIntegrationExpires) {
            nextIntegrationExpires = expires;
          }


          let svcIds = Object.keys(integration.fleetMappings);

          for (let j = 0; j < svcIds.length; j++) {
            let entityId = svcIds[j];
            let fleetUnitId = integration.fleetMappings[entityId];

            if (!!entityId && !!fleetUnitId) {
              let fleetUnit = fleet.units.find(t => t.id === fleetUnitId);
              if (!!fleetUnit && fleetUnit.lng !== 0 && fleetUnit.lat !== 0) {
                fleetData.set(entityId, { ...fleetUnit, lastModified: new Date(fleetUnit.lastUpdate) })
              }
            }
          }
        }

        setFleetUnits(fleetData);
      }
      catch (err) {
        console.log(err);
      }

    };

    const timer = setInterval(fetch, 15 * 1000);
    fetch();

    return () => {
      destroyed = true;
      clearInterval(timer);
    }
  }, [getAccessTokenSilently, fleetIntegrations]);



  return useMemo(() => {
    return vehicles
      .map(vehicle => {

        // Fetch and check fleet unit
        const fleetUnit = fleetUnits.get(vehicle.id);
        if (!fleetUnit) return vehicle;

        let lastModifiedEntity = new Date(vehicle.lastModified);
        const useEntityCoords = lastModifiedEntity > fleetUnit.lastModified && hasLocation(vehicle);

        // merge fields
        return {
          ...vehicle,
          fleetUnitData: fleetUnit,
          lastReportedLocation: useEntityCoords ? vehicle.lastReportedLocation : {
            ...vehicle.lastReportedLocation,
            timestamp: Date.now(),
            coords: {
              accuracy: 10,
              heading: typeof fleetUnit.speed === "number" && fleetUnit.speed > 1 ? fleetUnit.direction : undefined,
              latitude: fleetUnit.lat,
              longitude: fleetUnit.lng
            }
          }
        }
      }).map(vehicle => ({
        ...vehicle,
        tasks: activeTasks.filter(t => !!t.execution?.driver?.id && t.execution?.driver?.id === vehicle.driver?.id)
      }));
  }, [vehicles, fleetUnits, activeTasks]);
}

function hasLocation(serviceVehicle) {
  if (!serviceVehicle || !serviceVehicle.lastReportedLocation || !serviceVehicle.lastReportedLocation.coords) return false;

  const coords = serviceVehicle.lastReportedLocation.coords;
  const lat = coords.latitude;
  const lng = coords.longitude;

  return (typeof lat === "number" && lat !== 0) && (typeof lng === "number" && lat !== 0);
}